import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ReactComponent as CloseIcon } from 'applicaiton/assets/close.svg';
import { ReactComponent as AddIcon } from 'applicaiton/assets/add.svg';
import TimePicker from 'common/components/TimePicker';
import { TimeWrapper, Wrapper, DividerStyled } from './styles';
import { HoursRangeProps } from './modules';
import { TimeSlots } from 'common/types/clinics';

/**
 * A React component that allows users to add, edit, and remove time slots.
 *
 * @param defaultDate - The default date for all time slots.
 * @param slots - An array of time slots.
 * @param handleEditSlot - A function that is called when a time slot is edited.
 * @param handleAddSlot - A function that is called when a new time slot is added.
 * @param handleRemoveSlot - A function that is called when a time slot is removed.
 *
 */

const HoursRange: FC<HoursRangeProps> = ({
  defaultDate,
  slots,
  handleEditSlot,
  handleAddSlot,
  handleRemoveSlot,
}) => {
  const { t } = useTranslation();

  /**
   * Returns the minimum start time for a given index.
   *
   * @param startTime - The current start time of the slot being edited.
   * @param index - The index of the slot being edited.
   *
   */
  const getMinStartTime = (startTime: string, index: number) => {
    if (!slots.length || index === 0) return null;

    return dayjs(
      !startTime
        ? `${dayjs().format('YYYY-MM-DD')}T${slots[index - 1].endTime}`
        : `${defaultDate}T${slots[index - 1].endTime}`,
    );
  };

  /**
   * Returns the minimum end time for a given start time.
   *
   * @param startTime - The current start time of the slot being edited.
   *
   */
  const getMinEndTime = (startTime: string) => {
    const incrementedTime = dayjs(startTime).minute() + 5;
    return dayjs(startTime).set('minutes', incrementedTime);
  };

  /**
   * Determines if a given time is invalid for a given index.
   *
   * @param index - The index of the slot being edited.
   * @param time - The time to check.
   *
   */
  const isInvalidValue = (index: number, time: string) => {
    return slots.some((slot, i) => {
      if (i !== index) {
        if (slot.startTime === '' || slot.endTime === '') {
          return true;
        }

        return (slot.startTime || '') < time && (slot.endTime || '') > time;
      } else {
        if (slot.startTime === '' || slot.endTime === '') {
          return true;
        }
      }

      return false;
    });
  };

  /**
   * Handles changes to a time picker.
   *
   * @param value - The new time value.
   * @param index - The index of the slot being edited.
   * @param key - The key of the property to update (either "startTime" or "endTime").
   */
  const handleChange = (
    value: string | null,
    index: number,
    key: keyof TimeSlots,
  ) => {
    handleEditSlot(index, {
      [key]: value ? dayjs(value).format('HH:mm') : '',
    });
  };

  return (
    <Wrapper>
      <div>
        {slots.map((item, index) => (
          <TimeWrapper key={index}>
            <TimePicker
              defaultValue={dayjs(`${defaultDate}T${item.startTime}`)}
              value={
                item.startTime
                  ? dayjs(`${defaultDate}T${item.startTime}`)
                  : null
              }
              onChange={(value) => handleChange(value, index, 'startTime')}
              minTime={getMinStartTime(item.startTime, index)}
              isError={
                isInvalidValue(index, item.startTime!) || !item.startTime
              }
            />
            <DividerStyled />
            <TimePicker
              defaultValue={dayjs(defaultDate + item.endTime)}
              value={item.endTime ? dayjs(defaultDate + item.endTime) : null}
              minTime={getMinEndTime(`${defaultDate}T${item.startTime}`)}
              maxTime={dayjs(defaultDate).hour(23).minute(59)}
              onChange={(value) => handleChange(value, index, 'endTime')}
              isError={isInvalidValue(index, item.endTime!) || !item.endTime}
            />
            <button onClick={() => handleRemoveSlot(index)}>
              <CloseIcon />
            </button>
          </TimeWrapper>
        ))}
        <button type="button" onClick={() => handleAddSlot()}>
          <AddIcon />
          <h2>{t('add_another')}</h2>
        </button>
      </div>
    </Wrapper>
  );
};

export default HoursRange;
